import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { Sentry } from "../instrument";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    const error = new Error(`${res.status}: ${text}`);
    // Report 5xx (and unexpected 4xx that aren't auth) to Sentry — these
    // indicate a real backend problem, not user flow. 401/403 are routine.
    if (res.status >= 500 || (res.status >= 400 && res.status !== 401 && res.status !== 403 && res.status !== 404)) {
      Sentry.captureException(error, {
        tags: { status: String(res.status), url: res.url },
      });
    }
    throw error;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

// Retry only safe reads across short gateway outages. Mutations stay single-shot.
const READ_RETRY_DELAYS = [2_000, 5_000, 10_000];
function waitForReadRetry(delay: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    const abort = () => {
      clearTimeout(timer);
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, delay);
    signal?.addEventListener('abort', abort, { once: true });
  });
}
async function fetchQueryRead(url: string, signal?: AbortSignal): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    const res = await fetch(url, { credentials: 'include', signal });
    if (![502, 503, 504].includes(res.status) || attempt >= READ_RETRY_DELAYS.length) return res;
    await res.body?.cancel();
    await waitForReadRetry(READ_RETRY_DELAYS[attempt], signal);
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey, signal }) => {
    const res = await fetchQueryRead(queryKey.join("/") as string, signal);

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
